/**
 * ObraFormModal — Modal reutilizable para crear / editar una obra.
 * Usado en la página de Obras (Ingeniería) e inline en el editor de presupuestos.
 * Persiste contra /api/listas/obras. El código OB-<CL>-<NNNN>-<RR>-<AA> lo genera
 * el backend. Una obra siempre pertenece a un cliente (catálogo /api/listas/clientes).
 *
 * Props:
 *  - initial: obra existente para editar (null = nueva)
 *  - clientes: lista de clientes para el dropdown
 *  - preselectClienteId: cliente pre-seleccionado (creación rápida desde Presupuesto)
 *  - onClose(): cerrar sin guardar
 *  - onSaved(obra): callback con la obra creada/actualizada
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Plus } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { toast } from "sonner";
import ClienteFormModal from "./ClienteFormModal";

const inputCls =
  "w-full px-3 py-2 text-[13px] rounded-xl border border-gray-200 bg-white focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/20";

export default function ObraFormModal({ initial, clientes = [], preselectClienteId = "", onClose, onSaved }) {
  const [f, setF] = useState({
    obra: initial?.obra || "",
    cliente_id: initial?.cliente_id || preselectClienteId || "",
    direccion: initial?.direccion || "",
    observaciones: initial?.observaciones || "",
  });
  const [saving, setSaving] = useState(false);
  const [extraClientes, setExtraClientes] = useState([]);
  const [showNewCliente, setShowNewCliente] = useState(false);

  const clientesAll = [...clientes, ...extraClientes].reduce((acc, c) => {
    if (c && c._id && !acc.some((x) => x._id === c._id)) acc.push(c);
    return acc;
  }, []);

  const handleClienteCreated = (nc) => {
    if (nc && nc._id) {
      setExtraClientes((prev) => [...prev.filter((c) => c._id !== nc._id), nc]);
      setF((prev) => ({ ...prev, cliente_id: nc._id }));
    }
    setShowNewCliente(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.cliente_id) { toast.error("Debés elegir (o crear) un cliente para la obra"); return; }
    if (!f.obra.trim()) { toast.error("El nombre de la obra es obligatorio"); return; }
    setSaving(true);
    try {
      const body = {
        obra: f.obra,
        cliente_id: f.cliente_id,
        direccion: f.direccion,
        observaciones: f.observaciones,
      };
      let data;
      if (initial?._id) {
        const r = await axios.put(`${API}/listas/obras/${initial._id}`, body);
        data = r.data;
        toast.success("Obra actualizada");
      } else {
        const r = await axios.post(`${API}/listas/obras`, body);
        data = r.data;
        toast.success(`Obra creada · ${data.codigo}`);
      }
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar la obra");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <h3 className="text-[15px] font-bold text-gray-900">
            {initial ? `Editar Obra · ${initial.codigo}` : "Nueva Obra"}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="obra-modal-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 overflow-y-auto space-y-3" data-testid="obra-form">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[11.5px] font-semibold text-gray-700 uppercase tracking-wide">
                Cliente <span className="text-red-500">*</span>
              </label>
              <button type="button" onClick={() => setShowNewCliente(true)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2E6DA4] hover:text-[#1f4f78]"
                data-testid="obra-new-cliente-btn">
                <Plus size={12} /> Nuevo cliente
              </button>
            </div>
            <select className={inputCls} value={f.cliente_id}
              onChange={(e) => setF({ ...f, cliente_id: e.target.value })}
              data-testid="obra-cliente-select">
              <option value="">-- Seleccionar cliente --</option>
              {clientesAll.map((c) => (
                <option key={c._id} value={c._id}>{`${c.codigo} — ${c.cliente}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
              Nombre de Obra <span className="text-red-500">*</span>
            </label>
            <input className={inputCls} value={f.obra} autoFocus
              onChange={(e) => setF({ ...f, obra: e.target.value })}
              placeholder="Ej: Edificio Costa Sole"
              data-testid="obra-input-nombre" />
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Dirección</label>
            <input className={inputCls} value={f.direccion}
              onChange={(e) => setF({ ...f, direccion: e.target.value })}
              placeholder="Av. Mcal. López 1234, Asunción"
              data-testid="obra-input-direccion" />
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Observaciones</label>
            <textarea className={`${inputCls} resize-y min-h-[64px]`} value={f.observaciones}
              onChange={(e) => setF({ ...f, observaciones: e.target.value })}
              placeholder="Notas internas de la obra..."
              data-testid="obra-input-observaciones" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold text-gray-600 hover:bg-gray-100">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#2E6DA4] hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="obra-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Guardar"}
            </button>
          </div>
        </form>
      </div>
      {showNewCliente && (
        <ClienteFormModal initial={null} onClose={() => setShowNewCliente(false)} onSaved={handleClienteCreated} />
      )}
    </div>,
    document.body
  );
}
